import api, { handleResponse, handleError } from './api';

const eventService = {
  /**
   * Get events with optional status filter
   * @param {string} [status] - Filter by status ('draft', 'published', 'greenlighted', 'completed', 'cancelled')
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getEvents(status) {
    try {
      const params = status ? { status } : {};
      const response = await api.get('/events', { params });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get a single event by ID
   * @param {string} id - Event ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getEvent(id) {
    try {
      const response = await api.get(`/events/${id}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get an event by its invite code
   * @param {string} inviteCode - Event invite code
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getEventByCode(inviteCode) {
    try {
      const response = await api.get(`/events/code/${inviteCode}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Create a new event
   * @param {object} data - Event data
   * @param {string} data.title - Event title
   * @param {string} data.description - Event description
   * @param {string} data.date - Event date (ISO string)
   * @param {string} data.location - Event location
   * @param {number} data.capacity - Maximum attendees
   * @param {number} [data.coverCharge] - Cover charge amount
   * @param {string} [data.dressCode] - Dress code
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async createEvent(data) {
    try {
      const response = await api.post('/events', data);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Update an existing event
   * @param {string} id - Event ID
   * @param {object} data - Event data to update
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async updateEvent(id, data) {
    try {
      const response = await api.put(`/events/${id}`, data);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Delete an event
   * @param {string} id - Event ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async deleteEvent(id) {
    try {
      const response = await api.delete(`/events/${id}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Publish an event (change status from draft to published)
   * @param {string} id - Event ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async publishEvent(id) {
    try {
      const response = await api.post(`/events/${id}/publish`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Greenlight an event (confirm it will happen)
   * @param {string} id - Event ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async greenlightEvent(id) {
    try {
      const response = await api.post(`/events/${id}/greenlight`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Cancel an event
   * @param {string} id - Event ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async cancelEvent(id) {
    try {
      const response = await api.post(`/events/${id}/cancel`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Upload a poster image for an event
   * @param {string} id - Event ID
   * @param {File} file - The image file to upload
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async uploadPoster(id, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/events/${id}/poster`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
  /**
   * Get invitable friends for an event
   * @param {string} eventId - Event ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getInvitableFriends(eventId) {
    try {
      const response = await api.get(`/events/${eventId}/invitable-friends`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Invite a friend to an event
   * @param {string} eventId - Event ID
   * @param {string} userId - User ID of friend to invite
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async inviteFriend(eventId, userId) {
    try {
      const response = await api.post(`/events/${eventId}/invite`, { userId });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async agreeToHouseRules(eventId) {
    try {
      const response = await api.post(`/events/${eventId}/house-rules/agree`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async claimPartySupply(supplyId, claim) {
    try {
      const response = await api.post(`/events/party-supplies/${supplyId}/claim`, { claim });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async uploadPartySupplyImage(supplyId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/events/party-supplies/${supplyId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
};

export default eventService;
